import { useState, useRef, useEffect } from "react"
import type { Renderer, Tester } from "react-insta-stories/dist/interfaces"
import { WithSeeMore } from "react-insta-stories"
import type Hls from "hls.js"
import Unmute from "~/icons/Unmute"
import Mute from "~/icons/Mute"

export const VideoRenderer: Renderer = ({
  story,
  action,
  isPaused,
  config,
  messageHandler
}) => {
  const [muted, setMuted] = useState(true)
  const { storyStyles } = config

  const computedStyles = {
    ...styles.storyContent,
    ...(storyStyles || {})
  }

  const vid = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)

  useEffect(() => {
    if (vid.current) {
      if (isPaused) {
        vid.current.pause()
      } else {
        vid.current.play().catch(() => {})
      }
    }
  }, [isPaused])

  // const onWaiting = () => {
  //   action("pause", true)
  // }

  const onPlaying = () => {
    action("play", true)
  }

  useEffect(() => {
    let isMounted = true

    if (!vid.current) {
      return
    }

    if (
      vid.current.canPlayType("application/vnd.apple.mpegurl") === "probably"
    ) {
      vid.current.src = story.url || ""
    } else {
      // Lazy load hls.js only when needed
      import("hls.js")
        .then(({ default: Hls }) => {
          if (!isMounted || !vid.current) {
            return
          }
          if (Hls.isSupported()) {
            // Clean up previous hls instance if it exists
            hlsRef.current?.destroy()
            const hls = new Hls()
            hlsRef.current = hls
            hls.loadSource(story.url || "")
            hls.attachMedia(vid.current)
          } else {
            console.error("This is a legacy browser that doesn't support MSE")
          }
        })
        .catch(error => {
          console.error("Failed to load hls.js:", error)
        })
    }

    return () => {
      isMounted = false
      hlsRef.current?.destroy()
      hlsRef.current = null
    }
  }, [story.url])

  const videoLoaded = () => {
    messageHandler("UPDATE_VIDEO_DURATION", { duration: vid.current?.duration })
    vid.current
      ?.play()
      .then(() => {
        action("play")
      })
      .catch(() => {
        setMuted(true)
        vid.current?.play().finally(() => {
          action("play")
        })
      })
  }

  if (!config) return null

  return (
    <WithSeeMore {...{ story, action }}>
      <div style={styles.videoContainer}>
        <video
          ref={vid}
          style={computedStyles}
          src={story.url}
          controls={false}
          onLoadedData={videoLoaded}
          playsInline
          // onWaiting={onWaiting}
          onPlaying={onPlaying}
          muted={muted}
          autoPlay
        />
        {muted ? (
          <button
            onClick={() => setMuted(false)}
            className="absolute left-5 top-7 z-max h-8 w-8 rounded-full bg-white"
          >
            <Mute className="ml-[8px] h-[16px] w-[16px]" />
          </button>
        ) : (
          <button
            onClick={() => setMuted(true)}
            className="absolute left-5 top-7 z-max h-8 w-8 rounded-full bg-white"
          >
            <Unmute className="ml-[8px] h-[14px] w-[14px]" />
          </button>
        )}
      </div>
    </WithSeeMore>
  )
}

const styles = {
  storyContent: {
    margin: "auto"
  },
  videoContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  }
}

export const tester: Tester = story => {
  return {
    condition: story.type === "video",
    priority: 2
  }
}

export default {
  renderer: VideoRenderer,
  tester
}
